import { useState } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2, Megaphone, Target, BarChart3, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useSEO } from "@/hooks/useSEO";
import { Overline, Breadcrumbs } from "@/components/shared";

const PLACEMENTS = ["Homepage Hero", "Homepage Mid Banner", "Sidebar Square", "In-Feed Square",
  "Multiple Placements", "Custom Campaign", "Sponsored Content", "Not Sure — Recommend Something"];

const OBJECTIVES = ["Brand Awareness", "Product Launch", "Lead Generation", "App / Tool Installs",
  "Website Traffic", "Sales / Conversions", "Event Promotion", "Other"];

const DURATIONS = ["1 week", "2 weeks", "1 month", "3 months", "6 months", "Ongoing", "Not sure yet"];

const BUDGETS = ["< $500 / month", "$500 – $1,000 / month", "$1,000 – $5,000 / month",
  "$5,000 – $10,000 / month", "$10,000+ / month", "Let's discuss"];

const CREATIVE = ["I have finished creative assets", "I have assets but need adaptation",
  "I need help creating the creative", "Not sure yet"];

const WHY = [
  { icon: Target, title: "A high-intent tech audience", body: "Reach readers actively researching AI tools, models, hardware and games — people ready to try and buy." },
  { icon: BarChart3, title: "Premium, clearly-labelled placements", body: "Sponsored slots across the homepage, in-feed and sidebar — always distinguishable, never disruptive." },
  { icon: Sparkles, title: "Editorial-grade presentation", body: "Your brand sits inside KLYROO's polished dark visual language, framed to feel native, not noisy." },
];

const EMPTY = {
  company_name: "", contact_name: "", email: "", phone: "", website: "",
  requested_placements: [], campaign_objective: "", target_audience: "", campaign_duration: "",
  budget_range: "", creative_status: "", message: "", reference_url: "", hp_field: "",
};
const inputCls = "w-full bg-klyroo-surface border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-klyroo-cyan/50 transition-colors";

export default function Advertise() {
  useSEO({ title: "Partner with KLYROO — Advertising", description: "Advertise with KLYROO and put your brand in front of a high-intent AI, gaming and technology audience. Tell us about your campaign and our team will be in touch." });
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: null })); };

  const togglePlacement = (p) => {
    setForm((f) => {
      const has = f.requested_placements.includes(p);
      return { ...f, requested_placements: has ? f.requested_placements.filter((x) => x !== p) : [...f.requested_placements, p] };
    });
  };

  const validate = () => {
    const e = {};
    if (!form.company_name.trim()) e.company_name = "Please enter your company / brand name.";
    else if (form.company_name.length > 200) e.company_name = "Too long (max 200 characters).";
    if (!form.contact_name.trim()) e.contact_name = "Please enter a contact person.";
    if (!form.email.trim()) e.email = "Please enter a business email.";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = "Enter a valid business email.";
    if (!form.message.trim()) e.message = "Please describe your campaign / advertising needs.";
    else if (form.message.length > 5000) e.message = "Too long (max 5000 characters).";
    if (form.website.trim() && !/^https?:\/\/[^\s]+\.[^\s]+/.test(form.website.trim())) e.website = "Enter a valid URL (starting with http).";
    if (form.reference_url.trim() && !/^https?:\/\/[^\s]+\.[^\s]+/.test(form.reference_url.trim())) e.reference_url = "Enter a valid URL (starting with http).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      const res = await api.submitAdvertising(form);
      // Only count a genuinely created request — not a duplicate/honeypot 200.
      if (res && res.created_new) track.advertisingRequestSubmit(form.requested_placements, res.id);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send your request. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <div className="klyroo-radial">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-klyroo-cyan/10 via-transparent to-klyroo-violet/10 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Advertise" }]} />
          <div className="inline-flex items-center gap-2 mt-4 px-3 py-1 rounded-full bg-klyroo-cyan/10 border border-klyroo-cyan/20 text-klyroo-cyan text-xs font-mono uppercase tracking-widest">
            <Megaphone size={13} /> Partner with KLYROO
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mt-5 max-w-3xl">
            Put your brand in front of a{" "}
            <span className="bg-gradient-to-r from-klyroo-cyan to-klyroo-violet bg-clip-text text-transparent">high-intent tech audience</span>
          </h1>
          <p className="text-slate-300 mt-5 text-lg max-w-2xl">
            KLYROO reaches readers actively researching AI tools, models, hardware and games. Tell us about your goals and our team will design a placement that fits.
          </p>
          <a href="#advertise-form" data-testid="advertise-hero-cta"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-klyroo-cyan to-klyroo-violet text-black font-semibold hover:opacity-90 transition-opacity">
            Start your campaign <Send size={16} />
          </a>
        </div>
      </div>

      {/* Why advertise */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid gap-6 md:grid-cols-3">
          {WHY.map(({ icon: Icon, title, body }) => (
            <div key={title} className="card-surface rounded-2xl p-6" data-testid="advertise-why-card">
              <div className="w-11 h-11 rounded-xl bg-klyroo-cyan/10 flex items-center justify-center text-klyroo-cyan"><Icon size={20} /></div>
              <h3 className="font-display text-lg font-bold text-white mt-4">{title}</h3>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Form */}
      <div id="advertise-form" className="max-w-3xl mx-auto px-4 sm:px-6 pb-20">
        {done ? (
          <div className="card-surface rounded-2xl p-8 text-center" data-testid="advertise-success">
            <CheckCircle2 size={48} className="text-klyroo-cyan mx-auto" />
            <h2 className="font-display text-2xl font-bold text-white mt-4">Thanks — we've received your request!</h2>
            <p className="text-slate-300 mt-3">Your advertising inquiry has been sent to the KLYROO partnerships team. We'll review it and get back to you at the email you provided.</p>
            <p className="text-slate-500 mt-2 text-sm">Nothing is published automatically — every campaign is reviewed and confirmed with you first.</p>
            <button data-testid="advertise-again" onClick={() => { setForm(EMPTY); setDone(false); }}
              className="mt-6 px-6 py-3 rounded-xl bg-klyroo-cyan text-black font-semibold hover:bg-white transition-colors">Send Another Request</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="card-surface rounded-2xl p-6 sm:p-8" data-testid="advertise-form-el">
            <Overline>Advertising inquiry</Overline>
            <h2 className="font-display text-2xl font-bold text-white mt-2">Tell us about your campaign</h2>
            <p className="text-slate-400 text-sm mt-2">Fields marked <span className="text-klyroo-cyan">*</span> are required. Everything else helps us tailor the right placement.</p>

            {/* Company info */}
            <SectionLabel>Company information</SectionLabel>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Company / brand name" required error={errors.company_name}>
                <input data-testid="adv-company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Acme Inc." className={inputCls} />
              </Field>
              <Field label="Contact person" required error={errors.contact_name}>
                <input data-testid="adv-contact" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Your name" className={inputCls} />
              </Field>
              <Field label="Business email" required error={errors.email}>
                <input data-testid="adv-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" className={inputCls} />
              </Field>
              <Field label="Phone" hint="Optional">
                <input data-testid="adv-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 555 000 0000" className={inputCls} />
              </Field>
            </div>
            <Field label="Company website" hint="Optional" error={errors.website}>
              <input data-testid="adv-website" type="url" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://…" className={inputCls} />
            </Field>

            {/* Requirements */}
            <SectionLabel>Placement & requirements</SectionLabel>
            <Field label="Which placements are you interested in?" hint="Select any that apply">
              <div className="flex flex-wrap gap-2" data-testid="adv-placements">
                {PLACEMENTS.map((p) => {
                  const on = form.requested_placements.includes(p);
                  return (
                    <button key={p} type="button" onClick={() => togglePlacement(p)} data-testid={`adv-placement-${p.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      aria-pressed={on}
                      className={`px-3 py-2 rounded-xl text-sm border transition-colors ${on ? "bg-klyroo-cyan text-black border-klyroo-cyan font-semibold" : "bg-klyroo-surface text-slate-300 border-white/10 hover:border-klyroo-cyan/40"}`}>{p}</button>
                  );
                })}
              </div>
            </Field>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Primary campaign objective" hint="Optional">
                <select data-testid="adv-objective" value={form.campaign_objective} onChange={(e) => set("campaign_objective", e.target.value)} className={inputCls}>
                  <option value="">Choose an objective…</option>
                  {OBJECTIVES.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Campaign duration" hint="Optional">
                <select data-testid="adv-duration" value={form.campaign_duration} onChange={(e) => set("campaign_duration", e.target.value)} className={inputCls}>
                  <option value="">Choose a duration…</option>
                  {DURATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Target audience" hint="Optional" help="Who are you trying to reach? (e.g. AI developers, gamers, enterprise buyers)">
              <input data-testid="adv-audience" value={form.target_audience} onChange={(e) => set("target_audience", e.target.value)} placeholder="e.g. AI developers and startup founders" className={inputCls} />
            </Field>

            {/* Budget */}
            <SectionLabel>Budget</SectionLabel>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Estimated budget" hint="Optional">
                <select data-testid="adv-budget" value={form.budget_range} onChange={(e) => set("budget_range", e.target.value)} className={inputCls}>
                  <option value="">Choose a range…</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
              <Field label="Creative assets" hint="Optional">
                <select data-testid="adv-creative" value={form.creative_status} onChange={(e) => set("creative_status", e.target.value)} className={inputCls}>
                  <option value="">Choose one…</option>
                  {CREATIVE.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>

            {/* Details */}
            <SectionLabel>Campaign details</SectionLabel>
            <Field label="Tell us about your campaign" required error={errors.message}>
              <textarea data-testid="adv-message" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)}
                placeholder="Describe what you'd like to promote, your goals, timing, and anything else that will help us recommend the right placement." className={inputCls} />
            </Field>
            <Field label="Reference link" hint="Optional" error={errors.reference_url} help="A product page, deck, or existing creative you'd like us to see.">
              <input data-testid="adv-reference" type="url" value={form.reference_url} onChange={(e) => set("reference_url", e.target.value)} placeholder="https://…" className={inputCls} />
            </Field>

            {/* honeypot */}
            <input tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.hp_field} onChange={(e) => set("hp_field", e.target.value)}
              className="absolute left-[-9999px] w-px h-px opacity-0" name="hp_field" />

            <button type="submit" disabled={sending} data-testid="advertise-submit"
              className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-klyroo-cyan to-klyroo-violet text-black font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
              <Send size={16} /> {sending ? "Sending…" : "Send Advertising Request"}
            </button>
            <p className="text-xs text-slate-500 mt-3">We'll only use your details to respond to this inquiry. Nothing is published without your confirmation.</p>
          </form>
        )}
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return <div className="text-xs font-mono uppercase tracking-widest text-klyroo-cyan mt-8 mb-4 first:mt-0">{children}</div>;
}

function Field({ label, required, hint, help, error, children }) {
  return (
    <div className="mb-5 last:mb-0">
      <label className="flex items-center gap-2 text-sm font-medium text-slate-200 mb-1.5">
        {label} {required ? <span className="text-klyroo-cyan">*</span> : hint ? <span className="text-xs text-slate-500 font-normal">({hint})</span> : null}
      </label>
      {children}
      {help && !error && <p className="text-xs text-slate-500 mt-1.5">{help}</p>}
      {error && <p className="text-xs text-klyroo-red mt-1.5" data-testid="advertise-error">{error}</p>}
    </div>
  );
}
